export type IndentChoice = 2 | 3 | 4 | '\t';

export type ParseOk = { ok: true; value: any };
export type ParseErr = {
  ok: false;
  message: string;
  position: number | null;
  line: number | null;
  column: number | null;
};
export type ParseResult = ParseOk | ParseErr;

export const SAMPLE_JSON = `{
  "firstName": "John",
  "lastName": "Smith",
  "age": 32,
  "address": {
    "streetAddress": "21 2nd Street",
    "city": "New York",
    "state": "NY",
    "postalCode": "10021"
  },
  "phoneNumbers": [
    { "type": "home", "number": "212 555-1234" },
    { "type": "fax", "number": "646 555-4567" }
  ]
}`;

export function offsetToLineCol(text: string, offset: number): { line: number; column: number } {
  let line = 1;
  let column = 1;
  const max = Math.max(0, Math.min(offset, text.length));
  for (let i = 0; i < max; i++) {
    if (text.charCodeAt(i) === 10) {
      line += 1;
      column = 1;
    } else {
      column += 1;
    }
  }
  return { line, column };
}

export function lineColToOffset(text: string, line: number, column: number): number {
  let curLine = 1;
  let i = 0;
  while (i < text.length && curLine < line) {
    if (text.charCodeAt(i) === 10) curLine += 1;
    i += 1;
  }
  return Math.min(text.length, i + Math.max(0, column - 1));
}

function extractPosition(message: string, text: string): number | null {
  const pos = /position\s+(\d+)/i.exec(message);
  if (pos) return parseInt(pos[1], 10);
  const loc = /line\s+(\d+)\s+column\s+(\d+)/i.exec(message);
  if (loc) return lineColToOffset(text, parseInt(loc[1], 10), parseInt(loc[2], 10));
  return null;
}

function viError(message: string): string {
  const m = String(message || '');
  if (/unexpected end/i.test(m)) return 'JSON bị cắt giữa chừng (thiếu } hoặc ]).';
  if (/unexpected token/i.test(m) || /unexpected character/i.test(m)) {
    return m.replace(/^JSON\.parse:\s*/i, 'Lỗi cú pháp: ');
  }
  if (/unexpected string/i.test(m)) return 'Có chuỗi không đúng chỗ (thiếu dấu phẩy?).';
  if (/unexpected number/i.test(m)) return 'Có số không đúng chỗ (thiếu dấu phẩy?).';
  return m.replace(/^JSON\.parse:\s*/i, '');
}

export function parseJson(text: string): ParseResult {
  const raw = text == null ? '' : String(text);
  if (!raw.trim()) {
    return { ok: false, message: 'Chưa có dữ liệu JSON.', position: 0, line: 1, column: 1 };
  }
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch (err: any) {
    const message = (err && err.message) || 'JSON không hợp lệ.';
    const position = extractPosition(message, raw);
    const loc =
      position != null
        ? offsetToLineCol(raw, position)
        : (() => {
            const lc = /line\s+(\d+)\s+column\s+(\d+)/i.exec(message);
            return lc ? { line: parseInt(lc[1], 10), column: parseInt(lc[2], 10) } : null;
          })();
    return {
      ok: false,
      message: viError(message),
      position,
      line: loc ? loc.line : null,
      column: loc ? loc.column : null,
    };
  }
}

export function indentString(choice: IndentChoice): string {
  return choice === '\t' ? '\t' : new Array(choice + 1).join(' ');
}

export function stringifyJson(value: any, indent: IndentChoice): string {
  return JSON.stringify(value, null, indentString(indent));
}

export function minifyJson(value: any): string {
  return JSON.stringify(value);
}

function isIdentStart(c: string): boolean {
  return (c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_' || c === '$';
}

function isIdentPart(c: string): boolean {
  return isIdentStart(c) || (c >= '0' && c <= '9');
}

/** Strip // and /* comments while staying outside strings. */
export function stripComments(src: string): string {
  let out = '';
  let i = 0;
  const n = src.length;
  let inStr: '"' | "'" | null = null;
  let escape = false;
  while (i < n) {
    const c = src[i];
    const next = i + 1 < n ? src[i + 1] : '';
    if (inStr) {
      out += c;
      if (escape) escape = false;
      else if (c === '\\') escape = true;
      else if (c === inStr) inStr = null;
      i += 1;
      continue;
    }
    if (c === '"' || c === "'") {
      inStr = c;
      out += c;
      i += 1;
      continue;
    }
    if (c === '/' && next === '/') {
      i += 2;
      while (i < n && src[i] !== '\n') i += 1;
      continue;
    }
    if (c === '/' && next === '*') {
      i += 2;
      while (i + 1 < n && !(src[i] === '*' && src[i + 1] === '/')) i += 1;
      i += 2;
      out += ' ';
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}

function unwrapJsonp(src: string): string {
  const trimmed = src.trim();
  const m = /^([A-Za-z_$][\w$]*)\(\s*([\s\S]*)\s*\)\s*;?\s*$/.exec(trimmed);
  if (!m) return src;
  const inner = m[2].trim();
  if (inner.charAt(0) === '{' || inner.charAt(0) === '[') return inner;
  return src;
}

function convertSingleQuotedStrings(src: string): string {
  let out = '';
  let i = 0;
  const n = src.length;
  let inDouble = false;
  let escape = false;
  while (i < n) {
    const c = src[i];
    if (inDouble) {
      out += c;
      if (escape) escape = false;
      else if (c === '\\') escape = true;
      else if (c === '"') inDouble = false;
      i += 1;
      continue;
    }
    if (c === '"') {
      inDouble = true;
      out += c;
      i += 1;
      continue;
    }
    if (c === "'") {
      out += '"';
      i += 1;
      let esc = false;
      while (i < n) {
        const ch = src[i];
        if (esc) {
          out += ch === "'" ? "'" : `\\${ch}`;
          esc = false;
          i += 1;
          continue;
        }
        if (ch === '\\') {
          esc = true;
          i += 1;
          continue;
        }
        if (ch === "'") {
          i += 1;
          break;
        }
        if (ch === '"') out += '\\"';
        else if (ch === '\n') out += '\\n';
        else if (ch === '\r') out += '\\r';
        else out += ch;
        i += 1;
      }
      out += '"';
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}

function quoteUnquotedKeys(src: string): string {
  let out = '';
  let i = 0;
  const n = src.length;
  let inStr = false;
  let escape = false;
  let expectKey = false;
  const stack: Array<'obj' | 'arr'> = [];
  while (i < n) {
    const c = src[i];
    if (inStr) {
      out += c;
      if (escape) escape = false;
      else if (c === '\\') escape = true;
      else if (c === '"') inStr = false;
      i += 1;
      continue;
    }
    if (c === '"') {
      inStr = true;
      out += c;
      i += 1;
      continue;
    }
    if (c === '{') {
      stack.push('obj');
      expectKey = true;
      out += c;
      i += 1;
      continue;
    }
    if (c === '[') {
      stack.push('arr');
      expectKey = false;
      out += c;
      i += 1;
      continue;
    }
    if (c === '}' || c === ']') {
      stack.pop();
      expectKey = false;
      out += c;
      i += 1;
      continue;
    }
    if (c === ',') {
      expectKey = stack[stack.length - 1] === 'obj';
      out += c;
      i += 1;
      continue;
    }
    if (c === ':') {
      expectKey = false;
      out += c;
      i += 1;
      continue;
    }
    if (expectKey && (c === ' ' || c === '\n' || c === '\r' || c === '\t')) {
      out += c;
      i += 1;
      continue;
    }
    if (expectKey && isIdentStart(c)) {
      let j = i + 1;
      while (j < n && isIdentPart(src[j])) j += 1;
      let k = j;
      while (k < n && (src[k] === ' ' || src[k] === '\n' || src[k] === '\r' || src[k] === '\t')) k += 1;
      if (k < n && src[k] === ':') {
        out += `"${src.slice(i, j)}"`;
        i = j;
        expectKey = false;
        continue;
      }
    }
    out += c;
    i += 1;
  }
  return out;
}

function stripTrailingCommas(src: string): string {
  let out = '';
  let i = 0;
  const n = src.length;
  let inStr = false;
  let escape = false;
  while (i < n) {
    const c = src[i];
    if (inStr) {
      out += c;
      if (escape) escape = false;
      else if (c === '\\') escape = true;
      else if (c === '"') inStr = false;
      i += 1;
      continue;
    }
    if (c === '"') {
      inStr = true;
      out += c;
      i += 1;
      continue;
    }
    if (c === ',') {
      let k = i + 1;
      while (k < n && (src[k] === ' ' || src[k] === '\n' || src[k] === '\r' || src[k] === '\t')) k += 1;
      if (k < n && (src[k] === '}' || src[k] === ']')) {
        i += 1;
        continue;
      }
    }
    out += c;
    i += 1;
  }
  return out;
}

/** Best-effort fix for JS-ish / JSONC input. Does not send data anywhere. */
export function repairLooseJson(input: string): string {
  let s = input == null ? '' : String(input);
  if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
  s = unwrapJsonp(s);
  s = stripComments(s);
  s = convertSingleQuotedStrings(s);
  s = quoteUnquotedKeys(s);
  s = stripTrailingCommas(s);
  return s.trim();
}

export function sortValue(value: any): any {
  if (Array.isArray(value)) return value.map(sortValue);
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    const out: { [k: string]: any } = {};
    keys.forEach((k) => {
      out[k] = sortValue(value[k]);
    });
    return out;
  }
  return value;
}

export function utf8Bytes(text: string): number {
  const g: any = typeof global !== 'undefined' ? global : {};
  const Enc = g.TextEncoder || (g.window && g.window.TextEncoder);
  if (Enc) return new Enc().encode(text).length;
  return unescape(encodeURIComponent(text)).length;
}

export function countNodes(value: any): { keys: number; items: number } {
  let keys = 0;
  let items = 0;
  const walk = (v: any) => {
    if (Array.isArray(v)) {
      items += v.length;
      v.forEach(walk);
    } else if (v && typeof v === 'object') {
      const ks = Object.keys(v);
      keys += ks.length;
      ks.forEach((k) => walk(v[k]));
    }
  };
  walk(value);
  return { keys, items };
}

export function typeLabel(value: any): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

export function jsonPathJoin(parent: string, key: string | number, isIndex: boolean): string {
  const base = parent || '$';
  if (isIndex) return `${base}[${key}]`;
  const k = String(key);
  if (/^[A-Za-z_$][\w$]*$/.test(k)) return `${base}.${k}`;
  return `${base}[${JSON.stringify(k)}]`;
}

export function valuePreview(value: any): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `Array(${value.length})`;
  if (value && typeof value === 'object') return `Object(${Object.keys(value).length})`;
  return String(value);
}

export function matchesQuery(key: string, value: any, q: string): boolean {
  if (!q) return false;
  const needle = q.toLowerCase();
  if (String(key).toLowerCase().indexOf(needle) >= 0) return true;
  if (value === null) return needle === 'null';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).toLowerCase().indexOf(needle) >= 0;
  }
  return false;
}

export function subtreeHasQuery(value: any, q: string): boolean {
  if (!q) return false;
  if (matchesQuery('', value, q)) return true;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (subtreeHasQuery(value[i], q)) return true;
    }
    return false;
  }
  if (value && typeof value === 'object') {
    const ks = Object.keys(value);
    for (let i = 0; i < ks.length; i++) {
      const k = ks[i];
      if (matchesQuery(k, value[k], q) || subtreeHasQuery(value[k], q)) return true;
    }
  }
  return false;
}

export function isJsonStringPayload(value: any): boolean {
  return typeof value === 'string' && (value.charAt(0) === '{' || value.charAt(0) === '[' || value.charAt(0) === '"');
}

export async function copyText(text: string, win: any): Promise<boolean> {
  try {
    if (win.navigator && win.navigator.clipboard && win.navigator.clipboard.writeText) {
      await win.navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const doc = win.document;
    const ta = doc.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', 'true');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    doc.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = doc.execCommand('copy');
    doc.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
